import React from "react";
import classNames from "classnames";
import Image from "next/image";

import Link from "next/link";

import MagnifyingGlass from "assets/iconsv2/magnifying-glass-16.svg";
import ArrowRight from "assets/iconsv2/arrow-right-16.svg";

import useRecentSearches from "lib/global_search/useRecentSearches";

import HotKey from "../HotKey";

import styles from "./search.module.css";

import SearchSectionHeader from "./SearchSectionHeader";

type ExtensionSearchResultProptypes = {
  href: string;
  title: string;
  description: string;
  icon: string;
  char: string;
  currentlyFocussedId: string | undefined | null;
  searchInput: string;
};

const ExtensionSearchResult: React.FC<ExtensionSearchResultProptypes> = ({
  href,
  title,
  description,
  icon,
  char,
  currentlyFocussedId,
  searchInput,
}) => {
  const isFocussed = currentlyFocussedId === `result-${char}`;
  const [, addRecentSearch] = useRecentSearches();

  return (
    <a
      href={href}
      className={classNames(styles.extensionSearchResult, { [styles.isFocussed]: isFocussed })}
      onClick={() => addRecentSearch(searchInput)}
      data-hoverable-id={`result-${char}`}
    >
      <span className={styles.iconContainer}>
        {icon && <Image src={icon} alt={`${title} icon`} width={24} height={24} layout="fixed" />}
      </span>

      <div className={styles.extensionBody}>
        <strong>{title}</strong>
        <span>{description}</span>
      </div>

      {isFocussed && <HotKey char="↵" right={8} />}
    </a>
  );
};

const EmptyState = () => (
  <div className={styles.emptyState}>
    <MagnifyingGlass />
    <p>Can&apos;t find what you&apos;re looking for?</p>
    <a href="https://developers.raycast.com">
      Build your own Extension <ArrowRight className="icon" />
    </a>
  </div>
);

type SearchResult = {
  id: string;
  links: {
    self: string;
  };
  title: string;
  icons:
    | {
        dark: string;
        light?: string;
      }
    | {
        light: string;
        dark?: string;
      };
  description: string;
};

type SearchResultsPropTypes = {
  searchInput: string;
  searchResults: SearchResult[];
  currentlyFocussedId: string | undefined | null;
};

const SearchResults: React.FC<SearchResultsPropTypes> = ({ searchInput, searchResults, currentlyFocussedId }) => (
  <div>
    {searchResults.length ? (
      <div>
        <SearchSectionHeader>Results</SearchSectionHeader>
        <div className={styles.searchResultsContainer}>
          {searchResults.map(({ id, links, title, icons, description }, index) => (
            <ExtensionSearchResult
              key={id}
              href={links.self}
              title={title}
              icon={(icons.dark || icons.light) as string}
              description={description}
              char={(index + 1).toString()}
              currentlyFocussedId={currentlyFocussedId}
              searchInput={searchInput}
            />
          ))}
          <Link href={`/search?q=${searchInput}`}>
            <a
              className={classNames(styles.viewAllSearchResults, {
                [styles.isFocussed]: currentlyFocussedId === "result-all",
              })}
              data-hoverable-id={`result-all`}
            >
              <span>View all results</span>
              <ArrowRight className="icon" />
            </a>
          </Link>
        </div>
      </div>
    ) : (
      <EmptyState />
    )}
  </div>
);

export default SearchResults;
